import { Badge, Form, FormInstance, Input, Skeleton } from 'antd';
import {
  CompanyInvoiceSettingsForm,
  InvoiceSettingsData,
} from '@/core/domain/invoice/settings/Settings.types';
import InvoiceDetails from './InvoiceDetails';
import { useCompanyFormContext } from '../CompaniesFormProvider';
import CompanyInvoiceNFe from './InvoiceNFe';
import CompanyInvoiceNFc from './InvoiceNFc';
import CompanyInvoiceNFs from './InvoiceNFs';
import { useQueryClient } from 'react-query';
import { useEffect, useState } from 'react';
import './style.less';
import InvoiceQuotaFcpSettings from './fields/InvoiceQuotaFcpSettings';
import TabsCustom from '@/components/_common/TabsCustom';

type Props = {
  form: FormInstance<CompanyInvoiceSettingsForm>;
  tabErrors: Record<string, boolean>;
  onSubmit: () => void;
  loadingValidate: boolean;
};

const CompanyInvoiceForm: React.FC<Props> = ({
  form,
  tabErrors,
  onSubmit,
  loadingValidate,
}) => {
  const { isLoading, companyId } = useCompanyFormContext();
  const queryClient = useQueryClient();
  const invoiceSettings = queryClient.getQueryData<InvoiceSettingsData>([
    'invoiceSettings/company',
    companyId,
  ]);
  const [menuKeyActive, setMenuKeyActive] = useState<string>('details');

  const formValues = Form.useWatch([], form);

  const isMain =
    typeof invoiceSettings?.isAnotherCompanyActive === 'undefined' ||
    invoiceSettings?.isAnotherCompanyActive;

  const hasNFe =
    typeof formValues?.issueNfe === 'boolean'
      ? formValues?.issueNfe
      : !!invoiceSettings?.issueNfe;

  const hasNFc =
    typeof formValues?.issueNfc === 'boolean'
      ? formValues?.issueNfc
      : !!invoiceSettings?.issueNfc;

  const hasNFs =
    typeof formValues?.issueNfs === 'boolean'
      ? formValues?.issueNfs
      : !!invoiceSettings?.issueNfs;

  useEffect(() => {
    if (!isMain) {
      setMenuKeyActive('certificate');
    } else {
      setMenuKeyActive('details');
    }
  }, [isMain]);

  return (
    <Form form={form} name="companyInvoice">
      <Form.Item hidden name="id">
        <Input />
      </Form.Item>

      <Form.Item hidden name="nfe" />
      <Form.Item hidden name="nfc" />
      <Form.Item hidden name="nfs" />
      <Form.Item hidden name="cfe" />

      <Form.Item hidden name="nfsLogin" />
      <Form.Item hidden name="nfsPassword" />

      <Skeleton loading={isLoading}>
        <TabsCustom
          key={menuKeyActive}
          activeKey={menuKeyActive}
          onChange={setMenuKeyActive}
        >
          {isMain && (
            <TabsCustom.Item
              icon={tabErrors?.details ? <Badge dot /> : null}
              label="Detalhes"
              key="details"
            >
              <InvoiceDetails
                form={form}
                invoiceSettingsId={invoiceSettings?.id}
              />
            </TabsCustom.Item>
          )}

          {isMain && hasNFe && (
            <TabsCustom.Item
              icon={tabErrors?.invoiceNfe ? <Badge dot /> : null}
              label="NF-e"
              key="invoiceNfe"
            >
              <CompanyInvoiceNFe
                form={form}
                configNfe={invoiceSettings?.configNfe?.id}
              />
            </TabsCustom.Item>
          )}

          {isMain && hasNFc && (
            <TabsCustom.Item
              icon={tabErrors?.invoiceNfc ? <Badge dot /> : null}
              label="NFC-e"
              key="invoiceNfc"
            >
              <CompanyInvoiceNFc
                form={form}
                configNfc={invoiceSettings?.configNfc?.id}
              />
            </TabsCustom.Item>
          )}

          {isMain && hasNFs && (
            <TabsCustom.Item
              icon={tabErrors?.invoiceNfs ? <Badge dot /> : null}
              label="NFS-e"
              key="invoiceNfs"
            >
              <CompanyInvoiceNFs
                form={form}
                configNfs={invoiceSettings?.configNfs?.id}
                onSubmit={onSubmit}
                loadingValidate={loadingValidate}
              />
            </TabsCustom.Item>
          )}

          {isMain && (
            <TabsCustom.Item
              icon={tabErrors?.aliquot ? <Badge dot /> : null}
              label="Alíquota FCP"
              key="aliquot"
            >
              {invoiceSettings?.id && (
                <InvoiceQuotaFcpSettings
                  invoiceSettingsId={invoiceSettings.id}
                />
              )}
            </TabsCustom.Item>
          )}
        </TabsCustom>
      </Skeleton>
    </Form>
  );
};

export default CompanyInvoiceForm;
